import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import AggregateRatingBlock from "../components/AggregateRatingBlock";
import GoogleReviewsWidget from "../components/GoogleReviewsWidget";
import TestimonialGallery from "../components/TestimonialGallery";
import CaseStudyIndex from "../components/CaseStudyIndex";
import TestimonialSubmissionForm from "../components/TestimonialSubmissionForm";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function SuccessStories() {
  useDocumentTitle("Success Stories");
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[12%] bottom-10 hidden sm:block" size={36} delay={0.5} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Success Stories
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Stories from the Coast
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Real change for the fishermen who supply us, and real difference for the people who
            cook what they catch.
          </motion.p>

          <div className="mt-8 flex flex-col items-center gap-4">
            <AggregateRatingBlock />
            <GoogleReviewsWidget />
          </div>
        </div>
      </section>

      <Breadcrumbs />

      {/* Testimonial gallery — filterable by customer/producer/driver, city, theme */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">
            Voices from Our Network
          </h2>
        </ScrollReveal>

        <div className="mt-12">
          <TestimonialGallery />
        </div>
      </section>

      {/* Case studies */}
      <section className="bg-foam-100 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Case Studies</h2>
            <p className="mt-4 text-ocean-800/70">
              Structured results for partners and investors evaluating ZenFresh with data.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <CaseStudyIndex />
          </div>
        </div>
      </section>

      {/* Submission form */}
      <section id="share-your-story" className="mx-auto max-w-2xl px-6 py-24">
        <ScrollReveal className="text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Share Your Story</h2>
          <p className="mt-4 text-ocean-800/70">
            Customer, fisherman, or delivery partner — we&apos;d love to feature your experience.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1} className="mt-10">
          <TestimonialSubmissionForm />
        </ScrollReveal>
      </section>
    </PageTransition>
  );
}
